import React, {useEffect, useState} from 'react';
import * as Yup from 'yup';
import {Row, Col} from 'react-bootstrap';
import {Input, Form, MainLoader, DropdownField} from '@components';
import {WhiteButton, GreenButton, RedButton, StyledH3} from '@styled';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {
  COUNTRIES_LIST,
  ENTITY_STATUSES,
  RELATION_TO_PARENT,
  ROLE_DATA_REPORTER,
  ROLE_EXECUTIVE_REPORTER,
} from '@app/utils/constants';
import {get, isNaN} from 'lodash';
import {sagaActions} from './redux/saga';
import EntitySummary from './EntitySummary';
import Activities from './Activities';
import ManageReporters from './ManageReporters';

const UpdateEntity = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.entity.formLoading);
  const loadedEntity = useSelector((state) => state.entity.loadedEntity);
  const parentEntities = useSelector((state) => state.entity.parentEntities);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const [has2ShowDetails, toggleShowDetails] = useState(false);
  const {entityId} = useParams();

  useEffect(() => {
    if (entityId) {
      dispatch({type: sagaActions.FETCH_ENTITY, payload: {values: {entityId}}});
    }
  }, [entityId]);

  useEffect(() => {
    if (loadedEntity) {
      if (currentUser.Roles.includes(ROLE_EXECUTIVE_REPORTER)) {
        toggleShowDetails(
          loadedEntity.address1 && loadedEntity.country && loadedEntity.city
            ? true
            : false
        );
        dispatch({
          type: sagaActions.FETCH_PARENT_ENTITIES,
          payload: {values: {entityType: loadedEntity.type}},
        });
      } else if (currentUser.Roles.includes(ROLE_DATA_REPORTER)) {
        toggleShowDetails(true);
      }
    }
  }, [loadedEntity]);

  const getDefaultParentEntity = React.useCallback(() => {
    return (
      parentEntities.find((item) => item.value === loadedEntity.parentEntity) ||
      ''
    );
  }, [parentEntities, loadedEntity]);

  const getDefaultRelationShip = React.useCallback(() => {
    return (
      RELATION_TO_PARENT.find(
        (item) => item.value === loadedEntity.relationToParent
      ) || ''
    );
  }, [loadedEntity]);

  const getDefaultStatus = React.useCallback(() => {
    return ENTITY_STATUSES.find(
      (item) => item.value === loadedEntity.entityStatus
    );
  }, [loadedEntity]);

  const getDefaultCountry = React.useCallback(() => {
    return (
      COUNTRIES_LIST.find((item) => item.value === loadedEntity.country) || ''
    );
  }, [loadedEntity]);

  const handleSubmit = (values) => {
    dispatch({
      type: sagaActions.UPDATE_ENTITY,
      payload: {values: {...values, entityId}},
    });
  };

  const isSubsidiary = () => {
    return loadedEntity.type === 'Subsidiary' ? true : false;
  };

  const isFacility = () => {
    return loadedEntity.type === 'Facility' ? true : false;
  };

  const renderFacilityDetails = () => {
    const parentEntityName = parentEntities.find(
      (item) => item.value === loadedEntity.parentEntity
    ) || {label: '-'};
    return (
      <>
        <Row className="border-dark border-bottom">
          <Col className="border-dark border-right">Name</Col>
          <Col>{loadedEntity.name}</Col>
        </Row>
        <Row className="border-dark border-bottom">
          <Col className="border-dark border-right">
            National Classification of Economic Activities(ISIC4)
          </Col>
          <Col>{loadedEntity.ecoActivityCode}</Col>
        </Row>
        <Row className="border-dark border-bottom">
          <Col className="border-dark border-right">Parent Entity</Col>
          <Col>{parentEntityName.label}</Col>
        </Row>
        <Row className="border-dark border-bottom">
          <Col className="border-dark border-right">City</Col>
          <Col>{loadedEntity.city}</Col>
        </Row>
        <Row className="border-dark border-bottom">
          <Col className="border-dark border-right">Country</Col>
          <Col>{loadedEntity.country}</Col>
        </Row>
        <Row className="border-dark border-bottom">
          <Col className="border-dark border-right">Street Address</Col>
          <Col>{loadedEntity.address1}</Col>
        </Row>
        <Row className="">
          <Col className="border-dark border-right">Postal Address</Col>
          <Col>{loadedEntity.address2}</Col>
        </Row>
      </>
    );
  };
  const renderSubsidiaryDetails = () => {
    return (
      <>
        <Row className="border-dark border-bottom">
          <Col className="border-dark border-right">
            Commercial Registration
          </Col>
          <Col>{loadedEntity.commercialRegistration}</Col>
        </Row>
        <Row className="border-dark border-bottom">
          <Col className="border-dark border-right">Trading Name</Col>
          <Col>{loadedEntity.tradingName}</Col>
        </Row>
        <Row className="border-dark border-bottom">
          <Col className="border-dark border-right">City</Col>
          <Col>{loadedEntity.city}</Col>
        </Row>
        <Row className="border-dark border-bottom">
          <Col className="border-dark border-right">Country</Col>
          <Col>{loadedEntity.country}</Col>
        </Row>
        <Row className="border-dark border-bottom">
          <Col className="border-dark border-right">Street Address</Col>
          <Col>{loadedEntity.address1}</Col>
        </Row>
        <Row className="">
          <Col className="border-dark border-right">Postal Address</Col>
          <Col>{loadedEntity.address2}</Col>
        </Row>
      </>
    );
  };

  const renderActivitiesAndReporters = () => {
    return isFacility() ? (
      <>
        <Activities entityId={loadedEntity.id} />
        {currentUser.Roles.includes(ROLE_EXECUTIVE_REPORTER) ? (
          <ManageReporters entityId={loadedEntity.id} />
        ) : null}
      </>
    ) : null;
  };

  const renderEntityDetails = () => {
    return has2ShowDetails ? (
      <div>
        <Row className="mb-2">
          <Col>
            <h6>
              {loadedEntity.type} Details{' '}
              {currentUser.Roles.includes(ROLE_EXECUTIVE_REPORTER) ? (
                <WhiteButton onClick={() => toggleShowDetails(false)}>
                  Update Details
                </WhiteButton>
              ) : null}
            </h6>
          </Col>
        </Row>
        <div
          style={{backgroundColor: '#fff', padding: '0px 10px'}}
          className="border border-dark"
        >
          {isSubsidiary() ? renderSubsidiaryDetails() : renderFacilityDetails()}
        </div>
        <EntitySummary
          entityId={loadedEntity.id}
          entityType={loadedEntity.type}
        />

        {renderActivitiesAndReporters()}
      </div>
    ) : null;
  };

  const renderUpdateForm = () => {
    return !has2ShowDetails ? (
      <Form
        initialValues={{
          entityType: get(loadedEntity, 'type', ''),
          parentEntity: getDefaultParentEntity(),
          relationToParentEntity: getDefaultRelationShip(),
          entityName: get(loadedEntity, 'name', ''),
          entityStatus: getDefaultStatus(),
          ecoActivityCode: get(loadedEntity, 'ecoActivityCode', ''),
          commercialRegistration: get(
            loadedEntity,
            'commercialRegistration',
            ''
          ),
          tradingName: get(loadedEntity, 'tradingName', ''),
          address1: get(loadedEntity, 'address1', ''),
          address2: get(loadedEntity, 'address2', ''),
          address3: get(loadedEntity, 'address3', ''),
          city: get(loadedEntity, 'city', ''),
          country: getDefaultCountry(),
          postalCode: get(loadedEntity, 'postalCode', ''),
        }}
        validationSchema={Yup.object({
          parentEntity: Yup.object().required(
            t('reporter.entity.required.parentEntity')
          ),
          relationToParentEntity: Yup.object().required(
            t('reporter.entity.required.relationToParentEntity')
          ),
          entityName: Yup.string().required(
            t('reporter.entity.required.entityName')
          ),
          entityStatus: Yup.object().required(
            t('reporter.entity.required.entityStatus')
          ),
          commercialRegistration: Yup.string().when('entityType', {
            is: (cc) => cc === 'Subsidiary',
            then: Yup.string()
              .required(t('reporter.entity.required.commercialRegistration'))
              .test({
                name: 'is-valid-cr-number',
                message: t('reporter.entity.required.invalidCRNumber'),
                test: (value) => {
                  return value && /^[a-z0-9]+$/i.test(value) ? true : false;
                },
              }),
          }),
          ecoActivityCode: Yup.string().when('entityType', {
            is: (cc) => cc === 'Facility',
            then: Yup.string()
              .required(t('reporter.entity.required.ecoActivityCode'))
              .test({
                name: 'is-valid-eco-act-number',
                message: t('reporter.entity.required.invalidEcoActivityCode'),
                test: (value) => {
                  return !isNaN(Number(value)) ? true : false;
                },
              }),
          }),
          address1: Yup.string().required(
            t('reporter.entity.required.address1')
          ),
          address2: Yup.string().required(
            t('reporter.entity.required.address2')
          ),
          city: Yup.string().required(t('reporter.entity.required.city')),
          country: Yup.object().required(t('reporter.entity.required.country')),
          postalCode: Yup.string()
            .required(t('reporter.entity.required.postalCode'))
            .test({
              name: 'is-valid-postal-code',
              message: t('reporter.entity.required.invalidPostalCode'),
              test: (value) => {
                return value && /^[a-z0-9]+$/i.test(value) ? true : false;
              },
            }),
        })}
        onSubmit={(values) => {
          handleSubmit(values);
        }}
        enableReinitialize
      >
        <Row>
          <Col>
            <StyledH3>
              {t('reporter.entity.entityInformation')} (
              {get(loadedEntity, 'type', '')})
            </StyledH3>
          </Col>
        </Row>
        <div className="card">
          <div className="card-body">
            <Row className="mb-2">
              <Col xs={12} lg={6}>
                <Input
                  type="text"
                  name="entityName"
                  id="entityName"
                  label={t('reporter.entity.entityName')}
                  placeholder=""
                  required
                />
              </Col>
              <Col xs={12} lg={6}>
                <DropdownField
                  id="parentEntity"
                  name="parentEntity"
                  label={t('reporter.entity.parentEntity')}
                  options={parentEntities}
                  placeholder=""
                  required
                  menuPortalTarget="parent"
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs={12} lg={6}>
                <DropdownField
                  id="entityStatus"
                  name="entityStatus"
                  label={t('reporter.entity.entityStatus')}
                  options={ENTITY_STATUSES}
                  placeholder=""
                  required
                  menuPortalTarget="parent"
                />
              </Col>
              <Col xs={12} lg={6}>
                <DropdownField
                  id="relationToParentEntity"
                  name="relationToParentEntity"
                  label={t('reporter.entity.relationToParentEntity')}
                  options={RELATION_TO_PARENT}
                  placeholder=""
                  required
                  menuPortalTarget="parent"
                />
              </Col>
            </Row>
            {get(loadedEntity, 'type', '') === 'Subsidiary' && (
              <Row className="mb-2">
                <Col xs={12} lg={6}>
                  <Input
                    type="text"
                    name="commercialRegistration"
                    id="commercialRegistration"
                    label={t('reporter.entity.commercialRegistration')}
                    placeholder=""
                    required
                  />
                </Col>
                <Col xs={12} lg={6}>
                  <Input
                    type="text"
                    name="tradingName"
                    id="tradingName"
                    label={t('reporter.entity.tradingName')}
                    placeholder=""
                  />
                </Col>
              </Row>
            )}
            {get(loadedEntity, 'type', '') === 'Facility' && (
              <Row className="mb-2">
                <Col xs={12} lg={6}>
                  <Input
                    type="text"
                    name="ecoActivityCode"
                    id="ecoActivityCode"
                    label={t('reporter.entity.ecoActivityCode')}
                    placeholder=""
                    required
                  />
                </Col>
                <Col xs={12} lg={6}>
                  <a
                    href="/the_national_classification_of_economic_activities_aug-2019_isic4.pdf"
                    className="btn btn-link mt-lg-4 mt-0 text-left"
                    target="_blank"
                  >
                    The National Classification of Economic Activities
                  </a>
                  <div className="text-muted" style={{fontSize: '12px'}}>
                    * Please download the The National Classification of
                    Economic Activities file and enter the relevant activity
                    number for e.g. 011200 for Rice cultivation)
                  </div>
                </Col>
              </Row>
            )}
          </div>
        </div>
        <Row>
          <Col>
            <StyledH3>
              {t('reporter.entity.streetAddressSectionTitle')}
            </StyledH3>
          </Col>
        </Row>
        <div className="card">
          <div className="card-body">
            <Row>
              <Col xs={12} lg={6}>
                <Input
                  type="text"
                  name="address1"
                  id="address1"
                  label={t('reporter.entity.address1')}
                  placeholder=""
                  required
                />
              </Col>
              <Col xs={12} lg={6}>
                <Input
                  type="text"
                  name="address2"
                  id="address2"
                  label={t('reporter.entity.address2')}
                  placeholder=""
                  required
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} lg={6}>
                <Input
                  type="text"
                  name="address3"
                  id="address3"
                  label={t('reporter.entity.address3')}
                  placeholder=""
                />
              </Col>
              <Col xs={12} lg={6}>
                <Input
                  type="text"
                  name="city"
                  id="city"
                  label={t('reporter.entity.city')}
                  placeholder=""
                  required
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} lg={6}>
                <DropdownField
                  id="country"
                  name="country"
                  label={t('reporter.entity.country')}
                  options={COUNTRIES_LIST}
                  placeholder=""
                  required
                  menuPortalTarget="parent"
                />
              </Col>
              <Col xs={12} lg={6}>
                <Input
                  type="text"
                  name="postalCode"
                  id="postalCode"
                  label={t('reporter.entity.postalCode')}
                  placeholder=""
                  required
                />
              </Col>
            </Row>
          </div>
        </div>
        <Row className="mt-4">
          <Col>
            <input type="hidden" name="entityType" />
            <GreenButton type="submit" className="mr-1">
              {t('reporter.entity.updateEntitySubmitLabel')}
            </GreenButton>
            <RedButton type="button" className="ml-1">
              {t('reporter.entity.removeEntitySubmitLabel')}
            </RedButton>
            <WhiteButton
              type="button"
              className="ml-1"
              onClick={() => toggleShowDetails(true)}
            >
              {t('reporter.entity.addEntityCancelLabel')}
            </WhiteButton>
          </Col>
        </Row>
      </Form>
    ) : null;
  };

  return !loadedEntity ? (
    <div>Loading...</div>
  ) : (
    <>
      {isLoading && (
        <MainLoader
          minHeight="100vh"
          spinnerColor="#479541"
          background="rgba(0,0,0,0.2)"
        />
      )}
      {renderEntityDetails()}
      {renderUpdateForm()}
    </>
  );
};

export default UpdateEntity;
